// Small shared primitives used across BrandKit tab forms. Keeps the per-tab
// files focused on shape/payload concerns rather than markup.
export function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <label className="block text-xs uppercase tracking-[0.15em] text-[#6B6358] mb-1">
        {label}
      </label>
      {children}
    </div>
  );
}

export function TextInput({ value, onChange, testid, required, placeholder, type = "text" }) {
  return (
    <input
      data-testid={testid}
      type={type}
      required={required}
      placeholder={placeholder}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-3 py-2 border border-[#1A1F2C]/20 rounded-md bg-[#F8F5EE]"
    />
  );
}

export function TextArea({ value, onChange, testid, rows = 2, placeholder }) {
  return (
    <textarea
      data-testid={testid}
      rows={rows}
      placeholder={placeholder}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-3 py-2 border border-[#1A1F2C]/20 rounded-md bg-[#F8F5EE]"
    />
  );
}

export function NumberInput({ value, onChange, testid, step = "0.1", placeholder }) {
  return (
    <input
      data-testid={testid}
      type="number"
      step={step}
      placeholder={placeholder}
      value={value ?? ""}
      onChange={(e) => {
        const v = e.target.value;
        onChange(v === "" ? null : Number(v));
      }}
      className="w-full px-3 py-2 border border-[#1A1F2C]/20 rounded-md bg-[#F8F5EE]"
    />
  );
}

// Toggleable pill list — used for role tags and font weights / role bindings.
export function PillToggleGroup({ options, selected, onChange, testidPrefix }) {
  const set = new Set(selected || []);
  const toggle = (opt) => {
    const next = new Set(set);
    if (next.has(opt)) next.delete(opt);
    else next.add(opt);
    onChange(Array.from(next));
  };
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => {
        const active = set.has(opt);
        return (
          <button
            type="button"
            key={opt}
            data-testid={`${testidPrefix}${opt}`}
            onClick={() => toggle(opt)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
              active
                ? "bg-[#1A1F2C] text-[#F8F5EE] border-[#1A1F2C]"
                : "bg-transparent text-[#3D3A35] border-[#1A1F2C]/20 hover:border-[#1A1F2C]/50"
            }`}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}

export function FormActions({ submitting, onCancel, submitTestId, cancelTestId, submitLabel = "Save" }) {
  return (
    <div className="flex gap-3 mt-5">
      <button
        type="submit"
        data-testid={submitTestId}
        disabled={submitting}
        className="px-5 py-2 bg-[#1A1F2C] text-[#F8F5EE] rounded-full text-sm font-medium disabled:opacity-60"
      >
        {submitting ? "Saving…" : submitLabel}
      </button>
      <button
        type="button"
        data-testid={cancelTestId}
        onClick={onCancel}
        className="px-5 py-2 text-sm text-[#6B6358]"
      >
        Cancel
      </button>
    </div>
  );
}

// Toggleable pill list backed by a dynamic binding registry. Selected value
// is the binding slug; displayed label is the registry's `label`. Empty
// state nudges the admin towards M3+ template registration (the registry
// is meant to grow automatically).
export function BindingPillGroup({ bindings, selected, onChange, testidPrefix, loading }) {
  if (loading) {
    return <p className="text-xs text-[#6B6358]">Loading bindings…</p>;
  }
  if (!bindings.length) {
    return (
      <p className="text-xs text-[#6B6358]">
        No bindings registered yet. Templates registered in M3+ will populate this list automatically.
      </p>
    );
  }
  const set = new Set(selected || []);
  const toggle = (slug) => {
    const next = new Set(set);
    if (next.has(slug)) next.delete(slug); else next.add(slug);
    onChange(Array.from(next));
  };
  return (
    <div className="flex flex-wrap gap-2">
      {bindings.map((b) => {
        const active = set.has(b.slug);
        return (
          <button
            type="button"
            key={b.slug}
            title={b.description || b.slug}
            data-testid={`${testidPrefix}${b.slug}`}
            onClick={() => toggle(b.slug)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
              active
                ? "bg-[#1A1F2C] text-[#F8F5EE] border-[#1A1F2C]"
                : "bg-transparent text-[#3D3A35] border-[#1A1F2C]/20 hover:border-[#1A1F2C]/50"
            }`}
          >
            {b.label}
            {b.source === "user" && (
              <span className="ml-1.5 text-[9px] uppercase tracking-[0.18em] opacity-70">brand</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

// Generic multi-select chip group for arbitrary string IDs (e.g. category
// slugs, color IDs) — used for the disambiguation constraint fields.
export function MultiSelectChips({ options, selected, onChange, testidPrefix, emptyLabel = "Nothing to choose from." }) {
  if (!options.length) {
    return <p className="text-xs text-[#6B6358]">{emptyLabel}</p>;
  }
  const set = new Set(selected || []);
  const toggle = (id) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id); else next.add(id);
    onChange(Array.from(next));
  };
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => {
        const active = set.has(o.value);
        return (
          <button
            type="button"
            key={o.value}
            data-testid={testidPrefix ? `${testidPrefix}${o.value}` : undefined}
            onClick={() => toggle(o.value)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors flex items-center gap-1.5 ${
              active
                ? "bg-[#1A1F2C] text-[#F8F5EE] border-[#1A1F2C]"
                : "bg-transparent text-[#3D3A35] border-[#1A1F2C]/20 hover:border-[#1A1F2C]/50"
            }`}
          >
            {o.swatch && (
              <span
                aria-hidden
                className="w-3 h-3 rounded-sm border border-[#1A1F2C]/30"
                style={{ backgroundColor: o.swatch }}
              />
            )}
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
